import React from 'react';
import {
  GridColDef,
  GridColumnHeaderParams,
  GridRenderCellParams,
  GridTreeNodeWithRender,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import {numericFormatter} from 'react-number-format';
import {
  Box,
  TextField,
  Tooltip,
  TooltipProps,
  Typography,
  tooltipClasses,
  styled,
} from '@mui/material';
import {ApolloCache, DefaultContext, MutationFunctionOptions} from '@apollo/client';
import {useSnackbar} from 'notistack';
import {isNumber} from 'lodash';
import {Edit} from '@mui/icons-material';
import {usePlanFees} from './usePlanFees';
import {UpdateCharge, UpdateChargeVariables} from '../../schema-types/UpdateCharge';
import {themeColors} from '../../theme/Theme';
import {ChargeTypeFragment} from '../../schema-types/ChargeTypeFragment';
import {feesTooltipFormatter} from './feesTooltipFormatter';
import saltus from '../../shared/logger';

const ModelPortfolioFeeChargeTooltip: React.FC = () => {
  return (
    <table>
      <caption>Portfolio Name: Foo</caption>
      <thead>
        <tr>
          <th>Underlying fund costs</th>
          <th>Ex-ante Transaction Costs</th>
          <th>Saltus Platform ETF Broker charges</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>-</td>
          <td>-</td>
          <td>-</td>
        </tr>
      </tbody>
    </table>
  );
};

const GenericFeeChargeTooltip: React.FC = () => {
  return (
    <table>
      <caption>Fee Schedule: Foo</caption>
      <thead>
        <tr>
          <th>£0 - £500k</th>
          <th>£500k - £1m</th>
          <th>£1m - £2m</th>
          <th>£2m</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>-</td>
          <td>-</td>
          <td>-</td>
          <td>-</td>
        </tr>
      </tbody>
    </table>
  );
};

const TableTooltip = styled(({className, ...props}: TooltipProps) => (
  <Tooltip {...props} classes={{popper: className}} />
))(({theme}) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.primary.main,
    color: 'black',
    border: '1px solid #dadde9',
    fontSize: 14,
    [`& table`]: {
      backgroundColor: 'white',
    },
    [`& th`]: {
      backgroundColor: theme.palette.background.default,
    },
    [`& caption`]: {
      fontSize: 20,
      color: 'white',
      fontWeight: 'bold',
    },
    [`& td`]: {
      textAlign: 'center',
    },
  },
}));

const CustomTooltip = styled(({className, ...props}: TooltipProps) => (
  <Tooltip {...props} classes={{popper: className}} />
))(({theme}) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.background.paper,
    color: 'black',
    border: '1px solid #dadde9',
    fontSize: 14,
  },
}));

const poundFormat = (val: number) =>
  `£${numericFormatter(String(val), {
    decimalScale: 2,
    thousandSeparator: ',',
    fixedDecimalScale: true,
  })}`;

const percentFormat = (val: number) =>
  `${numericFormatter(String(val), {
    fixedDecimalScale: true,
    decimalScale: 2,
  })}%`;

export type editableChargeCell = ChargeTypeFragment & {
  value?: string | number;
};

const getValue = (
  params: GridRenderCellParams<
    GridValidRowModel,
    editableChargeCell,
    any,
    GridTreeNodeWithRender
  >
) => {
  const objValue =
    typeof params.value?.value === 'number' ? Number(params.value?.value) : undefined;
  const val: number | undefined =
    typeof params.value === 'number' ? Number(params.value) : objValue;
  const tooltipText =
    typeof params.value === 'number' ? null : String(params.value?.tooltipText);

  return {val, tooltipText};
};

export const renderPound = (
  params: GridRenderCellParams<
    GridValidRowModel,
    editableChargeCell,
    any,
    GridTreeNodeWithRender
  >
) => {
  const {val} = getValue(params);
  return <Typography textAlign="center">{val ? poundFormat(val) : '-'}</Typography>;
};

const RenderEditCellCharge = (
  params: GridRenderCellParams<
    GridValidRowModel,
    editableChargeCell,
    any,
    GridTreeNodeWithRender
  >,
  updateCharge: (
    options?:
      | MutationFunctionOptions<
          UpdateCharge,
          UpdateChargeVariables,
          DefaultContext,
          ApolloCache<any>
        >
      | undefined
  ) => Promise<any>
) => {
  const {enqueueSnackbar} = useSnackbar();
  if (params.isEditable) {
    saltus.log({params});
  }
  function updateValue(eventValue: string) {
    const eventNumber = Number(eventValue);
    const valIsNumber = !Number.isNaN(eventNumber);
    if (!valIsNumber || !eventValue) {
      enqueueSnackbar({
        message: 'Please type in a number',
        variant: 'error',
        preventDuplicate: true,
      });
      return;
    }
    if (valIsNumber && eventNumber < 0) {
      enqueueSnackbar({
        message: 'Value cannot be negative',
        variant: 'error',
        preventDuplicate: true,
      });
      return;
    }
    const value = {...params.value, value: Number(eventValue)};
    params.api.setEditCellValue({
      id: params.id,
      field: params.field,
      value,
    });
    if (!params.value?.id) {
      enqueueSnackbar({
        message: 'Charge ID missing',
        variant: 'error',
        preventDuplicate: true,
      });
      return;
    }
    updateCharge({
      variables: {
        id: params.value.id,
        annualPercentageValue: value.value,
      },
    });
  }
  return (
    <TextField
      variant="outlined"
      autoFocus
      size="small"
      sx={{bgcolor: 'white'}}
      fullWidth
      onBlur={(e) => {
        const eventValue = e.target.value;
        updateValue(eventValue);
      }}
      onKeyDown={(e) => {
        // for enter button
        if (e.key === 'Enter') {
          updateValue((e.target as any).value);
        }
      }}
    />
  );
};

const getTableTooltip = (
  params: GridRenderCellParams<
    GridValidRowModel,
    editableChargeCell,
    any,
    GridTreeNodeWithRender
  >
) => {
  if (params.value?.isEditable || !params.value?.tooltipText) {
    return null;
  }

  const {title, cells} = feesTooltipFormatter(params?.value?.tooltipText);

  return (
    <table>
      <caption style={{fontSize: '1rem', whiteSpace: 'prewrap'}}>{title}</caption>
      <thead>
        <tr>
          {cells.length === 0 && <></>}
          {cells.length === 2 && (
            <>
              <th>Frequency</th>
              <th>Amount</th>
            </>
          )}
          {cells.length === 3 && (
            <>
              <th>Underlying fund costs</th>
              <th>Ex-ante Transaction Costs</th>
              <th>Saltus Platform ETF Broker charges</th>
            </>
          )}
          {cells.length === 4 && (
            <>
              <th>£0 - £500k</th>
              <th>£500k - £1m</th>
              <th>£1m - £2m</th>
              <th>£2m</th>
            </>
          )}
        </tr>
      </thead>
      <tbody>
        <tr>
          {cells.length === 0 && <></>}
          {cells.length === 2 ? (
            <>
              <td>{cells[0]}</td>
              <td>£{cells[1]}</td>
            </>
          ) : (
            cells.length > 2 &&
            cells.map((cell) => {
              return <td key={cell}>{cell}%</td>;
            })
          )}
        </tr>
      </tbody>
    </table>
  );
};

const renderPercent = (
  params: GridRenderCellParams<
    GridValidRowModel,
    editableChargeCell,
    any,
    GridTreeNodeWithRender
  >
) => {
  const {val, tooltipText} = getValue(params);

  if (!params.isEditable) {
    return (
      <Box display="flex" justifyContent="center" p={1}>
        <TableTooltip title={getTableTooltip(params)}>
          <Typography textAlign="center">{val ? percentFormat(val) : '-'}</Typography>
        </TableTooltip>
      </Box>
    );
  }
  const valid = isNumber(val);
  saltus.log({params, val});
  const formattedVal = valid ? (
    percentFormat(val)
  ) : (
    <>
      <Edit
        htmlColor={themeColors.grey}
        fontSize="small"
        sx={{mr: 1, verticalAlign: 'middle'}}
      />
      Edit
    </>
  );

  return (
    <Box
      width="100%"
      border={`1px ${valid ? themeColors.green : themeColors.coral} solid`}
      borderRadius={0.8}
      alignContent="flex-end"
      justifyContent="center"
      display="flex"
      sx={{bgcolor: 'white'}}
      p={1}
    >
      <TableTooltip title={getTableTooltip(params)}>
        <Typography
          textAlign="center"
          color={valid ? themeColors.darkGreen : themeColors.coral}
        >
          <strong>{formattedVal}</strong>
        </Typography>
      </TableTooltip>
    </Box>
  );
};

const getCustomTooltip = (
  params: GridColumnHeaderParams<GridValidRowModel, editableChargeCell, any>
) => {
  const desc = params.colDef.description;
  return (
    <>
      <Typography variant="h5">{params.colDef.headerName}</Typography>
      {desc && <Typography fontStyle="italic">{desc}</Typography>}
    </>
  );
};

const renderHeader = (
  params: GridColumnHeaderParams<GridValidRowModel, editableChargeCell, any>
) => {
  return (
    <CustomTooltip title={getCustomTooltip(params)}>
      <Typography>{params.colDef.headerName}</Typography>
    </CustomTooltip>
  );
};

export const useOngoingFeesColumns: () => readonly GridColDef<GridValidRowModel>[] =
  () => {
    const {updateCharge} = usePlanFees();
    return [
      {
        field: 'potName',
        headerName: 'Plan Name',
        flex: 2,
        valueGetter(value) {
          return value || '-';
        },
        disableColumnMenu: true,
        renderHeader,
        renderCell: (params) => {
          return (
            <Box maxWidth="100%" sx={{lineHeight: '1.2rem'}}>
              <Typography sx={{whiteSpace: 'wrap'}}>{params.value || ''}</Typography>
            </Box>
          );
        },
      },
      {
        field: 'potValue',
        headerName: 'Plan Value',
        flex: 0.7,
        renderCell: renderPound,
        align: 'center',
        disableColumnMenu: true,
        renderHeader,
        headerAlign: 'center',
      },
      {
        field: 'sippFeeCharge',
        headerName: 'Provider',
        // headerName: 'Provider Charge',
        description:
          'Fee charged by the provider for managing the specific product, such as a SIPP',
        flex: 0.7,
        renderCell: renderPercent,
        align: 'center',
        editable: true,
        renderEditCell(params) {
          return RenderEditCellCharge(params, updateCharge);
        },
        disableColumnMenu: true,
        renderHeader,
        headerAlign: 'center',
      },
      {
        field: 'productFeeCharge',
        headerName: 'Platform',
        // headerName: 'Platform charge',
        description:
          'Fee charged by the platform provider for holding the plan investments',
        flex: 0.7,
        renderCell: renderPercent,
        align: 'center',
        editable: true,
        renderEditCell(params) {
          return RenderEditCellCharge(params, updateCharge);
        },
        disableColumnMenu: true,
        renderHeader,
        headerAlign: 'center',
      },
      {
        field: 'dfmFeeCharge',
        headerName: 'Investment fund',
        // headerName: 'Investment management charge',
        description:
          'Fee levied by the discretionary fund manager for managing the investments within the plan',
        flex: 0.7,
        renderCell: renderPercent,
        align: 'center',
        editable: true,
        renderEditCell(params) {
          return RenderEditCellCharge(params, updateCharge);
        },
        disableColumnMenu: true,
        renderHeader,
        headerAlign: 'center',
      },
      {
        field: 'modelPortfolioFeeCharge',
        headerName: 'Other fund',
        description: 'Other fund charges *',
        flex: 0.7,
        renderCell: renderPercent,
        align: 'center',
        editable: true,
        renderEditCell(params) {
          return RenderEditCellCharge(params, updateCharge);
        },
        disableColumnMenu: true,
        renderHeader,
        headerAlign: 'center',
      },
      {
        field: 'fpFeeCharge',
        headerName: 'FP charges',
        // headerName: 'Financial planning charge',
        description:
          'Fee for ongoing financial planning services provided by the advisor',
        flex: 0.7,
        renderCell: renderPercent,
        align: 'center',
        editable: true,
        renderEditCell(params) {
          return RenderEditCellCharge(params, updateCharge);
        },
        disableColumnMenu: true,
        renderHeader,
        headerAlign: 'center',
      },
      {
        field: 'totalChargePercentage',
        headerName: 'Total charge (%)',
        description:
          'Note: The "Total %" values are calculated using precise figures and may not match the sum of the displayed percentages due to rounding. This ensures the most accurate calculation of charges.',
        flex: 0.7,
        renderCell: renderPercent,
        align: 'center',
        disableColumnMenu: true,
        renderHeader,
        headerAlign: 'center',
      },
      {
        field: 'totalChargePound',
        headerName: 'Total charge (£)',
        description:
          'Note: The "Total £" values are derived from precise calculations and may not match the sum of the displayed values due to rounding. This ensures the most accurate representation of total charges.',
        flex: 0.7,
        renderCell: renderPound,
        align: 'center',
        disableColumnMenu: true,
        renderHeader,
        headerAlign: 'center',
      },
    ];
  };

export const initialFeesColumns: readonly GridColDef<GridValidRowModel>[] = [
  {
    field: 'name',
    headerName: 'Recommendation',
    flex: 0.7,
    valueGetter(value) {
      return value || '-';
    },
    disableColumnMenu: true,
  },
  {
    field: 'premiumAmount',
    headerName: 'Premium Amount',
    flex: 0.7,
    disableColumnMenu: true,
    renderCell: renderPound,
  },
  {
    field: 'initialFeeChargePercentage',
    headerName: 'Initial Fee (%)',
    flex: 0.7,
    disableColumnMenu: true,
    renderCell: renderPercent,
  },
  {
    field: 'initialFeeChargePound',
    headerName: 'Initial Fee (£)',
    flex: 0.7,
    disableColumnMenu: true,
    renderCell: renderPound,
  },
];
